import type {
  Area,
  Folder,
  Mutation,
  MutationEnvelope,
  Note,
  Project,
  ProjectStatus,
  Reminder,
  Synced,
  Task,
} from "@hq/contracts";
import { mutationTargetId } from "@hq/contracts";
import { newId, nextFireAt } from "@hq/core";
import type {
  EntityRepository,
  FolderRepository,
  KeyValueRepository,
  LocalStore,
  MutationRepository,
  NoteQuery,
  NoteRepository,
  PendingMutation,
  ProjectRepository,
  ReminderQuery,
  ReminderRepository,
  SearchRepository,
  SearchResult,
  SyncState,
  SyncStateRepository,
  TableName,
  TaskQuery,
  TaskRepository,
} from "@hq/local-store";
import { ChangeBus } from "@hq/local-store";
import type { SqlDriver, SqlValue } from "./driver";
import { runMigrations } from "./migrations";
import { toFtsQuery } from "./search";

type Stmt = { sql: string; params?: SqlValue[] };

/**
 * How a repository reaches the database. The root store serializes every call through a
 * queue and makes multi-statement writes atomic; a transaction-bound store runs directly on
 * the already-open transaction.
 */
interface Ctx {
  readonly driver: SqlDriver;
  readonly bus: ChangeBus;
  read<T>(fn: (driver: SqlDriver) => Promise<T>): Promise<T>;
  write(stmts: readonly Stmt[], ...tables: TableName[]): Promise<void>;
}

type EntityTable = Exclude<TableName, "mutations" | "sync_state" | "kv">;

/** Placeholders for an IN list. */
function marks(n: number): string {
  return Array.from({ length: n }, () => "?").join(", ");
}

const ID_CHUNK = 500;

class SqliteEntityRepo<T extends Synced> implements EntityRepository<T> {
  private readonly upsertSql: string;

  constructor(
    protected readonly ctx: Ctx,
    protected readonly table: EntityTable,
    columns: readonly string[],
    private readonly values: (entity: T) => SqlValue[],
  ) {
    const cols = ["id", ...columns, "deleted_at", "updated_at", "data"];
    const updates = cols
      .filter((c) => c !== "id")
      .map((c) => `${c} = excluded.${c}`)
      .join(", ");
    this.upsertSql = `INSERT INTO ${table} (${cols.join(", ")}) VALUES (${marks(cols.length)}) ON CONFLICT(id) DO UPDATE SET ${updates}`;
  }

  private upsert(entity: T): Stmt {
    return {
      sql: this.upsertSql,
      params: [
        entity.id,
        ...this.values(entity),
        entity.deletedAt ?? null,
        entity.updatedAt,
        JSON.stringify(entity),
      ],
    };
  }

  protected async select(where: string, params: SqlValue[] = [], suffix = ""): Promise<T[]> {
    return this.ctx.read(async (d) => {
      const rows = await d.query<{ data: string }>(
        `SELECT data FROM ${this.table} WHERE ${where} ORDER BY rowid${suffix}`,
        params,
      );
      return rows.map((r) => JSON.parse(r.data) as T);
    });
  }

  async get(id: string): Promise<T | undefined> {
    const rows = await this.select("id = ?", [id], " LIMIT 1");
    return rows[0];
  }

  async getMany(ids: readonly string[]): Promise<T[]> {
    if (ids.length === 0) return [];
    const byId = new Map<string, T>();
    for (let i = 0; i < ids.length; i += ID_CHUNK) {
      const chunk = ids.slice(i, i + ID_CHUNK);
      const rows = await this.select(`id IN (${marks(chunk.length)})`, [...chunk]);
      for (const r of rows) byId.set(r.id, r);
    }
    const out: T[] = [];
    for (const id of ids) {
      const e = byId.get(id);
      if (e) out.push(e);
    }
    return out;
  }

  async put(entity: T): Promise<void> {
    await this.ctx.write([this.upsert(entity)], this.table);
  }

  async putMany(entities: readonly T[]): Promise<void> {
    await this.ctx.write(
      entities.map((e) => this.upsert(e)),
      this.table,
    );
  }

  async all(): Promise<T[]> {
    return this.select("deleted_at IS NULL");
  }

  async trashed(): Promise<T[]> {
    return this.select("deleted_at IS NOT NULL");
  }
}

/** Build a `col IS NULL` / `col = ?` clause for nullable foreign keys. */
function eqOrNull(col: string, v: string | null, params: SqlValue[]): string {
  if (v === null) return `${col} IS NULL`;
  params.push(v);
  return `${col} = ?`;
}

function inList(col: string, values: readonly string[], params: SqlValue[]): string {
  if (values.length === 0) return "0";
  params.push(...values);
  return `${col} IN (${marks(values.length)})`;
}

class SqliteTaskRepo extends SqliteEntityRepo<Task> implements TaskRepository {
  constructor(ctx: Ctx) {
    super(
      ctx,
      "tasks",
      [
        "title",
        "description",
        "project_id",
        "status",
        "due_date",
        "scheduled_at",
        "parent_task_id",
      ],
      (t) => [
        t.title,
        t.description ?? null,
        t.projectId ?? null,
        t.status,
        t.dueDate ?? null,
        t.scheduledAt ?? null,
        t.parentTaskId ?? null,
      ],
    );
  }

  async list(q: TaskQuery = {}): Promise<Task[]> {
    const where = ["deleted_at IS NULL"];
    const params: SqlValue[] = [];
    if (q.projectId !== undefined) where.push(eqOrNull("project_id", q.projectId, params));
    if (q.status) where.push(inList("status", q.status, params));
    if (q.dueFrom !== undefined) {
      where.push("due_date >= ?");
      params.push(q.dueFrom);
    }
    if (q.dueTo !== undefined) {
      where.push("due_date <= ?");
      params.push(q.dueTo);
    }
    if (q.scheduledFrom !== undefined) {
      where.push("scheduled_at >= ?");
      params.push(q.scheduledFrom);
    }
    if (q.scheduledTo !== undefined) {
      where.push("scheduled_at < ?");
      params.push(q.scheduledTo);
    }
    if (q.parentTaskId !== undefined)
      where.push(eqOrNull("parent_task_id", q.parentTaskId, params));
    return this.select(where.join(" AND "), params);
  }
}

class SqliteNoteRepo extends SqliteEntityRepo<Note> implements NoteRepository {
  constructor(ctx: Ctx) {
    super(ctx, "notes", ["title", "markdown", "folder_id", "pinned"], (n) => [
      n.title,
      n.markdown,
      n.folderId ?? null,
      n.pinned ? 1 : 0,
    ]);
  }

  async list(q: NoteQuery = {}): Promise<Note[]> {
    const where = ["deleted_at IS NULL"];
    const params: SqlValue[] = [];
    if (q.folderId !== undefined) where.push(eqOrNull("folder_id", q.folderId, params));
    if (q.pinned !== undefined) {
      where.push("pinned = ?");
      params.push(q.pinned ? 1 : 0);
    }
    return this.select(where.join(" AND "), params);
  }

  async findByTitle(title: string): Promise<Note | undefined> {
    const rows = await this.select("deleted_at IS NULL AND title = ?", [title], " LIMIT 1");
    return rows[0];
  }
}

class SqliteFolderRepo extends SqliteEntityRepo<Folder> implements FolderRepository {
  constructor(ctx: Ctx) {
    super(ctx, "folders", ["parent_id"], (f) => [f.parentId ?? null]);
  }

  async list(q: { parentId?: string | null } = {}): Promise<Folder[]> {
    const where = ["deleted_at IS NULL"];
    const params: SqlValue[] = [];
    if (q.parentId !== undefined) where.push(eqOrNull("parent_id", q.parentId, params));
    return this.select(where.join(" AND "), params);
  }
}

class SqliteProjectRepo extends SqliteEntityRepo<Project> implements ProjectRepository {
  constructor(ctx: Ctx) {
    super(ctx, "projects", ["status"], (p) => [p.status]);
  }

  async list(q: { status?: readonly ProjectStatus[] } = {}): Promise<Project[]> {
    const where = ["deleted_at IS NULL"];
    const params: SqlValue[] = [];
    if (q.status) where.push(inList("status", q.status, params));
    return this.select(where.join(" AND "), params);
  }
}

class SqliteReminderRepo extends SqliteEntityRepo<Reminder> implements ReminderRepository {
  constructor(ctx: Ctx) {
    super(ctx, "reminders", ["status", "trigger_at", "fire_at", "target_id"], (r) => [
      r.status,
      r.triggerAt,
      nextFireAt(r) ?? null,
      "id" in r.target ? r.target.id : null,
    ]);
  }

  async list(q: ReminderQuery = {}): Promise<Reminder[]> {
    const where = ["deleted_at IS NULL"];
    const params: SqlValue[] = [];
    if (q.status) where.push(inList("status", q.status, params));
    if (q.targetId !== undefined) {
      where.push("target_id = ?");
      params.push(q.targetId);
    }
    if (q.dueBefore !== undefined) {
      where.push("fire_at IS NOT NULL AND fire_at <= ?");
      params.push(q.dueBefore);
    }
    return this.select(where.join(" AND "), params);
  }
}

type SearchRow = { id: string; title: string; snippet: string; rank: number; updated_at: string };

class SqliteSearchRepo implements SearchRepository {
  constructor(private readonly ctx: Ctx) {}

  private async run(
    fts: "notes_fts" | "tasks_fts",
    base: "notes" | "tasks",
    query: string,
    limit: number,
  ): Promise<SearchResult[]> {
    const match = toFtsQuery(query);
    if (match === undefined) return [];
    return this.ctx.read(async (d) => {
      const rows = await d.query<SearchRow>(
        `SELECT b.id AS id, b.title AS title, b.updated_at AS updated_at,
                snippet(${fts}, -1, '[', ']', '…', 12) AS snippet, ${fts}.rank AS rank
         FROM ${fts} JOIN ${base} b ON b.rowid = ${fts}.rowid
         WHERE ${fts} MATCH ? AND b.deleted_at IS NULL
         ORDER BY rank LIMIT ?`,
        [match, limit],
      );
      return rows.map((r) => ({
        id: r.id,
        title: r.title,
        snippet: r.snippet,
        rank: r.rank,
        updatedAt: r.updated_at,
      }));
    });
  }

  notes(query: string, options: { limit?: number } = {}): Promise<SearchResult[]> {
    return this.run("notes_fts", "notes", query, options.limit ?? 50);
  }

  tasks(query: string, options: { limit?: number } = {}): Promise<SearchResult[]> {
    return this.run("tasks_fts", "tasks", query, options.limit ?? 50);
  }
}

type MutationRow = {
  seq: number;
  mutation_id: string;
  device_id: string;
  created_at: string;
  mutation: string;
  attempts: number;
  attempted: number;
  last_error: string | null;
};

const MUTATION_COLUMNS = `seq, mutation_id, device_id, created_at, mutation, attempts, attempted, last_error`;

function toPending(r: MutationRow): PendingMutation {
  const out: PendingMutation = {
    mutationId: r.mutation_id,
    deviceId: r.device_id,
    createdAt: r.created_at,
    mutation: JSON.parse(r.mutation) as Mutation,
    seq: r.seq,
    attempts: r.attempts,
    attempted: r.attempted !== 0,
  };
  if (r.last_error !== null) out.lastError = r.last_error;
  return out;
}

class SqliteMutationRepo implements MutationRepository {
  constructor(private readonly ctx: Ctx) {}

  async enqueue(envelope: MutationEnvelope): Promise<void> {
    await this.ctx.write(
      [
        {
          sql: `INSERT OR IGNORE INTO mutations (mutation_id, device_id, created_at, target_id, mutation, attempts)
                VALUES (?, ?, ?, ?, ?, 0)`,
          params: [
            envelope.mutationId,
            envelope.deviceId,
            envelope.createdAt,
            mutationTargetId(envelope.mutation),
            JSON.stringify(envelope.mutation),
          ],
        },
      ],
      "mutations",
    );
  }

  async pending(limit = 100): Promise<PendingMutation[]> {
    return this.ctx.read(async (d) => {
      const rows = await d.query<MutationRow>(
        `SELECT ${MUTATION_COLUMNS} FROM mutations ORDER BY seq LIMIT ?`,
        [limit],
      );
      return rows.map(toPending);
    });
  }

  async count(): Promise<number> {
    return this.ctx.read(async (d) => {
      const rows = await d.query<{ n: number }>(`SELECT count(*) AS n FROM mutations`);
      return rows[0]?.n ?? 0;
    });
  }

  async acknowledge(mutationIds: readonly string[]): Promise<void> {
    if (mutationIds.length === 0) return;
    const stmts: Stmt[] = [];
    for (let i = 0; i < mutationIds.length; i += ID_CHUNK) {
      const chunk = mutationIds.slice(i, i + ID_CHUNK);
      stmts.push({
        sql: `DELETE FROM mutations WHERE mutation_id IN (${marks(chunk.length)})`,
        params: [...chunk],
      });
    }
    await this.ctx.write(stmts, "mutations");
  }

  async markAttempted(mutationIds: readonly string[]): Promise<void> {
    const statements: Stmt[] = [];
    for (let i = 0; i < mutationIds.length; i += ID_CHUNK) {
      const chunk = mutationIds.slice(i, i + ID_CHUNK);
      statements.push({
        sql: `UPDATE mutations SET attempted = 1 WHERE mutation_id IN (${marks(chunk.length)})`,
        params: [...chunk],
      });
    }
    await this.ctx.write(statements, "mutations");
  }

  async recordAttempt(mutationId: string, error: string | null): Promise<void> {
    await this.ctx.write(
      [
        {
          sql: `UPDATE mutations SET attempts = attempts + 1, last_error = ? WHERE mutation_id = ?`,
          params: [error, mutationId],
        },
      ],
      "mutations",
    );
  }

  async pendingTargetIds(): Promise<Set<string>> {
    return this.ctx.read(async (d) => {
      const rows = await d.query<{ target_id: string }>(`SELECT DISTINCT target_id FROM mutations`);
      return new Set(rows.map((r) => r.target_id));
    });
  }

  async pendingFor(targetId: string): Promise<PendingMutation[]> {
    return this.ctx.read(async (d) => {
      const rows = await d.query<MutationRow>(
        `SELECT ${MUTATION_COLUMNS} FROM mutations WHERE target_id = ? ORDER BY seq`,
        [targetId],
      );
      return rows.map(toPending);
    });
  }

  async replaceMutation(mutationId: string, mutation: Mutation): Promise<void> {
    await this.ctx.write(
      [
        {
          sql: `UPDATE mutations SET mutation = ?, target_id = ? WHERE mutation_id = ? AND attempted = 0`,
          params: [JSON.stringify(mutation), mutationTargetId(mutation), mutationId],
        },
      ],
      "mutations",
    );
  }
}

const SYNC_UPSERT = `INSERT INTO sync_state (key, value) VALUES (?, ?) ON CONFLICT(key) DO UPDATE SET value = excluded.value`;

class SqliteSyncRepo implements SyncStateRepository {
  constructor(private readonly ctx: Ctx) {}

  async get(): Promise<SyncState> {
    return this.ctx.read(async (d) => {
      const rows = await d.query<{ key: string; value: string }>(
        `SELECT key, value FROM sync_state`,
      );
      const state: Record<string, unknown> = { cursor: 0 };
      for (const r of rows) state[r.key] = JSON.parse(r.value);
      return state as SyncState;
    });
  }

  async update(patch: {
    [K in keyof Omit<SyncState, "deviceId">]?: SyncState[K] | undefined;
  }): Promise<void> {
    const stmts: Stmt[] = [];
    for (const [k, v] of Object.entries(patch)) {
      if (k === "deviceId") continue;
      if (v === undefined) stmts.push({ sql: `DELETE FROM sync_state WHERE key = ?`, params: [k] });
      else stmts.push({ sql: SYNC_UPSERT, params: [k, JSON.stringify(v)] });
    }
    await this.ctx.write(stmts, "sync_state");
  }
}

class SqliteKvRepo implements KeyValueRepository {
  constructor(private readonly ctx: Ctx) {}

  async get<T = unknown>(key: string): Promise<T | undefined> {
    return this.ctx.read(async (d) => {
      const rows = await d.query<{ value: string }>(`SELECT value FROM kv WHERE key = ?`, [key]);
      const row = rows[0];
      return row === undefined ? undefined : (JSON.parse(row.value) as T);
    });
  }

  async set(key: string, value: unknown): Promise<void> {
    await this.ctx.write(
      [
        {
          sql: `INSERT INTO kv (key, value) VALUES (?, ?) ON CONFLICT(key) DO UPDATE SET value = excluded.value`,
          params: [key, JSON.stringify(value ?? null)],
        },
      ],
      "kv",
    );
  }

  async delete(key: string): Promise<void> {
    await this.ctx.write([{ sql: `DELETE FROM kv WHERE key = ?`, params: [key] }], "kv");
  }
}

class SqliteStore implements LocalStore {
  readonly tasks: TaskRepository;
  readonly notes: NoteRepository;
  readonly folders: FolderRepository;
  readonly projects: ProjectRepository;
  readonly areas: EntityRepository<Area>;
  readonly reminders: ReminderRepository;
  readonly search: SearchRepository;
  readonly mutations: MutationRepository;
  readonly sync: SyncStateRepository;
  readonly kv: KeyValueRepository;

  private tail: Promise<unknown> = Promise.resolve();

  constructor(
    private readonly driver: SqlDriver,
    private readonly bus: ChangeBus,
    /** Set on the store handed to a `transaction` callback; it runs on the open transaction. */
    private readonly inTransaction: boolean,
  ) {
    const ctx: Ctx = {
      driver,
      bus,
      read: (fn) => (inTransaction ? fn(driver) : this.serialize(() => fn(driver))),
      write: (stmts, ...tables) =>
        inTransaction
          ? this.writeDirect(stmts, tables)
          : this.serialize(() => this.writeAtomic(stmts, tables)),
    };
    this.tasks = new SqliteTaskRepo(ctx);
    this.notes = new SqliteNoteRepo(ctx);
    this.folders = new SqliteFolderRepo(ctx);
    this.areas = new SqliteEntityRepo<Area>(ctx, "areas", [], () => []);
    this.projects = new SqliteProjectRepo(ctx);
    this.reminders = new SqliteReminderRepo(ctx);
    this.search = new SqliteSearchRepo(ctx);
    this.mutations = new SqliteMutationRepo(ctx);
    this.sync = new SqliteSyncRepo(ctx);
    this.kv = new SqliteKvRepo(ctx);
  }

  /** Root-store operations run one at a time so nothing interleaves with an open transaction. */
  private serialize<T>(fn: () => Promise<T>): Promise<T> {
    const next = this.tail.then(fn, fn);
    this.tail = next.catch(() => undefined);
    return next;
  }

  private async writeDirect(stmts: readonly Stmt[], tables: readonly TableName[]): Promise<void> {
    if (stmts.length === 1) await this.driver.exec(stmts[0]!.sql, stmts[0]!.params);
    else if (stmts.length > 1) await this.driver.batch([...stmts]);
    this.bus.touch(...tables);
  }

  /** Outside a transaction a multi-statement write gets its own BEGIN/COMMIT in one round trip. */
  private async writeAtomic(stmts: readonly Stmt[], tables: readonly TableName[]): Promise<void> {
    if (stmts.length <= 1) return this.writeDirect(stmts, tables);
    try {
      await this.driver.batch([{ sql: "BEGIN" }, ...stmts, { sql: "COMMIT" }]);
    } catch (err) {
      await this.driver.exec("ROLLBACK").catch(() => undefined);
      throw err;
    }
    this.bus.touch(...tables);
  }

  async transaction<T>(fn: (tx: LocalStore) => Promise<T>): Promise<T> {
    if (this.inTransaction) return fn(this);
    return this.serialize(async () => {
      await this.driver.exec("BEGIN");
      this.bus.begin();
      try {
        const result = await fn(new SqliteStore(this.driver, this.bus, true));
        await this.driver.exec("COMMIT");
        this.bus.commit();
        return result;
      } catch (err) {
        await this.driver.exec("ROLLBACK").catch(() => undefined);
        this.bus.rollback();
        throw err;
      }
    });
  }

  subscribe(tables: readonly TableName[], listener: () => void): () => void {
    return this.bus.subscribe(tables, listener);
  }

  async close(): Promise<void> {
    if (this.inTransaction) throw new Error("Cannot close the store inside a transaction");
    await this.serialize(() => this.driver.close());
  }
}

export type SqliteStoreOptions = {
  /** Used only when the database has no device id yet. */
  deviceId?: string;
};

/**
 * Open a LocalStore over any SqlDriver: runs pending migrations and makes sure a stable
 * device id exists in `sync_state`.
 */
export async function createSqliteStore(
  driver: SqlDriver,
  options: SqliteStoreOptions = {},
): Promise<LocalStore> {
  await runMigrations(driver);
  const rows = await driver.query<{ value: string }>(
    `SELECT value FROM sync_state WHERE key = 'deviceId'`,
  );
  if (rows.length === 0) {
    await driver.exec(SYNC_UPSERT, ["deviceId", JSON.stringify(options.deviceId ?? newId())]);
  }
  return new SqliteStore(driver, new ChangeBus(), false);
}
